// src/pages/AdminFormsPage.jsx — Bloque 4 v2 (layout IDE)
import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/api';

const font = "Montserrat, sans-serif";
const FIELD_TYPES = ['text', 'number', 'select', 'textarea', 'date', 'checkbox'];
const TYPE_CFG = {
  text:     { color: "#163244", icon: "Aa" },
  number:   { color: "#7b5ea7", icon: "##" },
  select:   { color: "#1b7f4e", icon: "▼"  },
  textarea: { color: "#b06a00", icon: "¶"  },
  date:     { color: "#185FA5", icon: "📅" },
  checkbox: { color: "#c0392b", icon: "☑"  },
};

const inputSx = {
  padding: "8px 11px", borderRadius: 8,
  border: "1.5px solid rgba(0,0,0,0.12)",
  fontSize: 12, fontFamily: font, color: "#163244",
  background: "#fff", outline: "none", width: "100%", boxSizing: "border-box",
};
const labelSx = {
  display: "block", fontSize: 9, fontWeight: 700,
  color: "#7a93a4", textTransform: "uppercase",
  letterSpacing: "0.06em", marginBottom: 4,
};

const emptyField = (serviceId) => ({
  service_id: serviceId, field_key: '', label: '',
  field_type: 'text', required: true, options: '', sort_order: 0,
});

// ─── FieldForm (inline editor) ────────────────────────────────────────────────
function FieldForm({ draft, onChange, onSave, onCancel, saving, title }) {
  const set = (key, val) => onChange(prev => ({ ...prev, [key]: val }));
  const cfg = TYPE_CFG[draft.field_type] || TYPE_CFG.text;

  return (
    <div style={{ background: "#fff", borderRadius: 12, border: `2px solid ${cfg.color}30`, overflow: "hidden" }}>
      {/* Header del editor */}
      <div style={{ background: `${cfg.color}08`, borderBottom: `1px solid ${cfg.color}20`, padding: "11px 16px", display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 24, height: 24, borderRadius: 6, background: cfg.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#fff", flexShrink: 0 }}>{cfg.icon}</div>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#163244" }}>{title}</span>
      </div>

      <div style={{ padding: "16px" }}>
        {/* Label + key */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <div>
            <label style={labelSx}>Label visible al usuario *</label>
            <input style={inputSx} value={draft.label || ''} onChange={e => set('label', e.target.value)} placeholder="Ej: Descripción del problema" />
          </div>
          <div>
            <label style={labelSx}>field_key (identificador) *</label>
            <input style={inputSx} value={draft.field_key || ''} onChange={e => set('field_key', e.target.value)} placeholder="Ej: descripcion_problema" />
          </div>
        </div>

        {/* Tipo + posición + requerido */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 100px", gap: 10, marginBottom: 10 }}>
          <div>
            <label style={labelSx}>Tipo de campo</label>
            <select style={{ ...inputSx, cursor: "pointer" }} value={draft.field_type || 'text'} onChange={e => set('field_type', e.target.value)}>
              {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={labelSx}>Posición</label>
            <input type="number" style={inputSx} value={draft.sort_order ?? 0} min={0} onChange={e => set('sort_order', Number(e.target.value))} />
          </div>
          <div>
            <label style={labelSx}>Requerido</label>
            <select style={{ ...inputSx, cursor: "pointer" }} value={draft.required ? 'true' : 'false'} onChange={e => set('required', e.target.value === 'true')}>
              <option value="true">Sí</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>

        {/* Opciones si es select */}
        {draft.field_type === 'select' && (
          <div style={{ marginBottom: 10 }}>
            <label style={labelSx}>Opciones (JSON array)</label>
            <input style={inputSx} value={draft.options || ''} onChange={e => set('options', e.target.value)} placeholder='["Opción 1","Opción 2","Opción 3"]' />
            <div style={{ fontSize: 9, color: "#aab8c2", marginTop: 3 }}>Formato: ["Op1", "Op2", "Op3"]</div>
          </div>
        )}

        {/* Preview del campo */}
        <div style={{ background: "#f5f7fa", borderRadius: 8, padding: "10px 12px", marginBottom: 12, border: "1px solid rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: "#aab8c2", textTransform: "uppercase", marginBottom: 6 }}>Preview</div>
          <div style={{ fontSize: 10, fontWeight: 600, color: "#163244", marginBottom: 4 }}>{draft.label || <span style={{ color: "#ccc" }}>Sin label</span>}{draft.required && <span style={{ color: "#c0392b" }}> *</span>}</div>
          {draft.field_type === 'textarea' && <div style={{ height: 36, borderRadius: 6, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", padding: "4px 8px", fontSize: 10, color: "#ccc" }}>Respuesta larga…</div>}
          {['text','number','date'].includes(draft.field_type) && <div style={{ height: 26, borderRadius: 6, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", padding: "4px 8px", fontSize: 10, color: "#ccc" }}>{draft.field_type === 'date' ? "dd/mm/aaaa" : "Respuesta…"}</div>}
          {draft.field_type === 'select' && (
            <div style={{ height: 26, borderRadius: 6, border: "1px solid rgba(0,0,0,0.1)", background: "#fff", padding: "4px 8px", fontSize: 10, color: "#ccc", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Seleccionar…</span><span>▼</span>
            </div>
          )}
          {draft.field_type === 'checkbox' && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: "#7a93a4" }}>
              <div style={{ width: 14, height: 14, borderRadius: 3, border: "1.5px solid rgba(0,0,0,0.2)", background: "#fff" }} /><span>{draft.label || "Opción"}</span>
            </div>
          )}
        </div>

        {/* Acciones */}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onCancel} disabled={saving} style={{ padding: "8px 18px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)", background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>Cancelar</button>
          <button onClick={onSave} disabled={saving} style={{ padding: "8px 22px", borderRadius: 50, border: "none", background: "#163244", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: saving ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            {saving ? <><span style={{ width: 10, height: 10, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />Guardando…</> : "Guardar campo"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FieldCard con Drag & Drop ───────────────────────────────────────────────
function FieldCard({ field, isEditing, editDraft, setEditDraft, onEdit, onCancelEdit, onSave, onDelete, saving, isDragging, isDragOver, onDragStart, onDragEnter, onDragOver, onDragLeave, onDrop, onDragEnd }) {
  const cfg = TYPE_CFG[field.field_type] || TYPE_CFG.text;

  if (isEditing) {
    return (
      <div style={{ background: "#fff", borderRadius: 10, border: `2px solid ${cfg.color}`, overflow: "hidden", opacity: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", background: cfg.color + "06" }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: cfg.color + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: cfg.color, flexShrink: 0 }}>{cfg.icon}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#163244" }}>{field.label}</div>
            <div style={{ fontSize: 9, color: cfg.color, fontWeight: 600 }}>✏ Editando en el panel derecho →</div>
          </div>
          <button onClick={e => { e.stopPropagation(); onCancelEdit(); }} style={{ padding: "4px 10px", borderRadius: 50, border: `1.5px solid ${cfg.color}30`, background: "transparent", color: cfg.color, fontFamily: font, fontWeight: 600, fontSize: 10, cursor: "pointer" }}>✕ Cancelar</button>
        </div>
      </div>
    );
  }

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      style={{
        background: "#fff",
        borderRadius: 10,
        border: isDragOver ? "2px solid #E7A432" : "1px solid rgba(0,0,0,0.07)",
        overflow: "hidden",
        transition: "box-shadow 0.15s, border 0.15s, opacity 0.15s, transform 0.15s",
        opacity: isDragging ? 0.35 : 1,
        transform: isDragOver ? "scale(1.01)" : "scale(1)",
        cursor: "grab",
        boxShadow: isDragOver ? "0 4px 20px rgba(231,164,50,0.2)" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px" }}>
        {/* Drag handle */}
        <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0, cursor: "grab", padding: "0 2px", opacity: 0.35 }}>
          {[0,1,2].map(i => (
            <div key={i} style={{ display: "flex", gap: 2 }}>
              <div style={{ width: 3, height: 3, borderRadius: "50%", background: "#7a93a4" }} />
              <div style={{ width: 3, height: 3, borderRadius: "50%", background: "#7a93a4" }} />
            </div>
          ))}
        </div>
        {/* Posición */}
        <div style={{ fontSize: 9, fontWeight: 700, color: "#aab8c2", minWidth: 18, textAlign: "center" }}>
          {field.sort_order}
        </div>
        {/* Tipo badge */}
        <div style={{ width: 28, height: 28, borderRadius: 7, background: cfg.color + "12", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: cfg.color, flexShrink: 0 }}>
          {cfg.icon}
        </div>
        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 12, color: "#163244", marginBottom: 3, display: "flex", alignItems: "center", gap: 5 }}>
            {field.label}
            {field.required && <span style={{ fontSize: 9, fontWeight: 700, color: "#c0392b" }}>*</span>}
          </div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            <span style={{ padding: "1px 7px", borderRadius: 20, fontSize: 9, fontWeight: 700, background: cfg.color + "12", color: cfg.color }}>{field.field_type}</span>
            <span style={{ padding: "1px 7px", borderRadius: 20, fontSize: 9, fontWeight: 600, background: "#f0f5f8", color: "#7a93a4", fontFamily: "monospace" }}>{field.field_key}</span>
            {field.options && <span style={{ padding: "1px 7px", borderRadius: 20, fontSize: 9, background: "#f0f5f8", color: "#7a93a4" }}>opciones</span>}
          </div>
        </div>
        {/* Acciones */}
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button onClick={e => { e.stopPropagation(); onEdit(); }} style={{ padding: "5px 12px", borderRadius: 50, border: "1.5px solid rgba(22,50,68,0.15)", background: "transparent", color: "#163244", fontFamily: font, fontWeight: 600, fontSize: 11, cursor: "pointer" }}>✏</button>
          <button onClick={e => { e.stopPropagation(); onDelete(); }} disabled={saving} style={{ padding: "5px 12px", borderRadius: 50, border: "1.5px solid rgba(192,57,43,0.15)", background: "transparent", color: "#c0392b", fontFamily: font, fontWeight: 600, fontSize: 11, cursor: "pointer" }}>🗑</button>
        </div>
      </div>
    </div>
  );
}

// ─── PreviewField — simula el campo como lo vería el cliente ─────────────────
function PreviewField({ field, isActive, isNew }) {
  const cfg   = TYPE_CFG[field.field_type] || TYPE_CFG.text;
  const opts  = (() => { try { return field.options ? JSON.parse(field.options) : []; } catch { return []; } })();
  const border = isActive ? `1.5px solid ${cfg.color}` : isNew ? "1.5px dashed rgba(231,164,50,0.5)" : "none";

  return (
    <div style={{ borderRadius: 8, padding: isActive || isNew ? "8px" : "0", border, background: isActive ? cfg.color + "04" : "transparent", transition: "all 0.2s" }}>
      <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: "#163244", marginBottom: 5 }}>
        {field.label || <span style={{ color: "#ccc" }}>Sin label</span>}
        {field.required && <span style={{ color: "#c0392b", marginLeft: 2 }}>*</span>}
      </label>

      {(field.field_type === 'text' || field.field_type === 'number') && (
        <div style={{ height: 28, borderRadius: 6, border: "1.5px solid rgba(0,0,0,0.1)", background: "#f9fbfc", padding: "0 8px", display: "flex", alignItems: "center" }}>
          <span style={{ fontSize: 10, color: "#ccc" }}>{field.field_type === 'number' ? "0" : "Escribir respuesta…"}</span>
        </div>
      )}
      {field.field_type === 'textarea' && (
        <div style={{ height: 44, borderRadius: 6, border: "1.5px solid rgba(0,0,0,0.1)", background: "#f9fbfc", padding: "6px 8px" }}>
          <span style={{ fontSize: 10, color: "#ccc" }}>Escribir descripción…</span>
        </div>
      )}
      {field.field_type === 'date' && (
        <div style={{ height: 28, borderRadius: 6, border: "1.5px solid rgba(0,0,0,0.1)", background: "#f9fbfc", padding: "0 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 10, color: "#ccc" }}>dd/mm/aaaa</span>
          <span style={{ fontSize: 10, color: "#ccc" }}>📅</span>
        </div>
      )}
      {field.field_type === 'select' && (
        <div style={{ height: 28, borderRadius: 6, border: "1.5px solid rgba(0,0,0,0.1)", background: "#f9fbfc", padding: "0 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 10, color: "#ccc" }}>{opts[0] || "Seleccionar…"}</span>
          <span style={{ fontSize: 8, color: "#ccc" }}>▼</span>
        </div>
      )}
      {field.field_type === 'checkbox' && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 14, height: 14, borderRadius: 3, border: "1.5px solid rgba(0,0,0,0.15)", background: "#f9fbfc", flexShrink: 0 }} />
          <span style={{ fontSize: 10, color: "#7a93a4" }}>{field.label || "Opción"}</span>
        </div>
      )}

      {/* Mini tag del tipo */}
      <div style={{ marginTop: 4, display: "flex", gap: 4 }}>
        <span style={{ fontSize: 8, padding: "0px 5px", borderRadius: 10, background: cfg.color + "10", color: cfg.color, fontWeight: 700 }}>{field.field_type}</span>
        {field.required && <span style={{ fontSize: 8, padding: "0px 5px", borderRadius: 10, background: "rgba(192,57,43,0.08)", color: "#c0392b", fontWeight: 700 }}>obligatorio</span>}
        {opts.length > 0 && <span style={{ fontSize: 8, padding: "0px 5px", borderRadius: 10, background: "#f0f5f8", color: "#7a93a4" }}>{opts.length} opciones</span>}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AdminFormsPage() {
  const [services,          setServices]          = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [fields,            setFields]            = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [saving,            setSaving]            = useState(false);
  const [error,             setError]             = useState('');
  const [success,           setSuccess]           = useState('');
  const [editingId,         setEditingId]         = useState(null);
  const [editDraft,         setEditDraft]         = useState({});
  const [newField,          setNewField]          = useState(null);
  const [svcSearch,         setSvcSearch]         = useState('');
  const [dragIdx,           setDragIdx]           = useState(null); // índice arrastrado
  const [dragOverIdx,       setDragOverIdx]       = useState(null); // índice sobre el que se pasa

  const fetchAll = async (currentServiceId) => {
    setLoading(true);
    try {
      const { data } = await api.get('/form-fields/admin/all');
      const list = data.services || [];
      setServices(list);
      setFields(list.find(s => s.id === currentServiceId)?.fields || []);
    } catch { setError('Error recargando campos'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get('/form-fields/admin/all')
      .then(({ data }) => {
        const list = data.services || [];
        setServices(list);
        if (list.length) { setSelectedServiceId(list[0].id); setFields(list[0].fields || []); }
      })
      .catch(() => setError('Error cargando formularios'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedServiceId || !services.length) return;
    setFields(services.find(s => s.id === selectedServiceId)?.fields || []);
    setEditingId(null); setNewField(null);
  }, [selectedServiceId, services]);

  const showSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const handleSaveEdit = async (fieldId) => {
    setSaving(true); setError('');
    try { await api.put(`/form-fields/admin/${fieldId}`, editDraft); showSuccess('Campo actualizado'); setEditingId(null); await fetchAll(selectedServiceId); }
    catch { setError('Error al guardar'); } finally { setSaving(false); }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm('¿Eliminar este campo?')) return;
    setSaving(true); setError('');
    try { await api.delete(`/form-fields/admin/${fieldId}`); showSuccess('Campo eliminado'); await fetchAll(selectedServiceId); }
    catch { setError('Error eliminando campo'); } finally { setSaving(false); }
  };

  const handleCreateField = async () => {
    if (!newField.label || !newField.field_key) { setError('Label y field_key son requeridos'); return; }
    setSaving(true); setError('');
    try {
      const svc = services.find(s => s.id === selectedServiceId);
      await api.post(`/form-fields/admin/${svc.id}`, newField);
      showSuccess('Campo creado'); setNewField(null); await fetchAll(selectedServiceId);
    } catch (err) { setError(err.response?.data?.message || 'Error creando campo'); }
    finally { setSaving(false); }
  };

  // ── Drag & drop reorder ───────────────────────────────────────────────────
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragEnd   = ()    => { setDragIdx(null); setDragOverIdx(null); };

  const handleDrop = async (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); setDragOverIdx(null); return; }
    // Reordenar localmente
    const reordered = [...fields];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    // Asignar sort_order incremental (10, 20, 30…)
    const withOrder = reordered.map((f, i) => ({ ...f, sort_order: (i + 1) * 10 }));
    setFields(withOrder);
    setDragIdx(null); setDragOverIdx(null);
    // Persistir en backend
    try {
      await api.put(`/form-fields/admin/${selectedServiceId}/reorder`, {
        order: withOrder.map(f => ({ id: f.id, sort_order: f.sort_order }))
      });
      showSuccess('Orden guardado');
      // Actualizar la lista de servicios en memoria para reflejar el nuevo orden
      setServices(prev => prev.map(s => s.id === selectedServiceId ? { ...s, fields: withOrder } : s));
    } catch { setError('Error al guardar el orden'); await fetchAll(selectedServiceId); }
  };

  const selectedSvc = services.find(s => s.id === selectedServiceId);
  const filteredSvcs = useMemo(() => services.filter(s => !svcSearch || s.service_name.toLowerCase().includes(svcSearch.toLowerCase())), [services, svcSearch]);

  // Stats por tipo de campo
  const typeStats = useMemo(() => {
    const counts = {};
    fields.forEach(f => { counts[f.field_type] = (counts[f.field_type] || 0) + 1; });
    return counts;
  }, [fields]);

  return (
    <>
      <div style={{ fontFamily: font, background: "#f5f7fa", minHeight: "100vh", display: "flex", flexDirection: "column" }}>

        {/* ── TOP BAR ─────────────────────────────────────────────── */}
        <div style={{ background: "#163244", padding: "14px 24px", display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>Editor de Formularios</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>{services.length} servicios · {services.reduce((a, s) => a + (s.fields?.length || 0), 0)} campos totales</div>
          </div>
          {error   && <div style={{ marginLeft: "auto", background: "rgba(192,57,43,0.15)", border: "1px solid rgba(192,57,43,0.3)", borderRadius: 8, padding: "7px 14px", fontSize: 12, color: "#ff8a80", fontWeight: 600, display: "flex", gap: 10, alignItems: "center" }}>⚠ {error} <button onClick={() => setError('')} style={{ background: "none", border: "none", cursor: "pointer", color: "#ff8a80", fontSize: 14, padding: 0 }}>✕</button></div>}
          {success && <div style={{ marginLeft: "auto", background: "rgba(27,127,78,0.15)", border: "1px solid rgba(27,127,78,0.3)", borderRadius: 8, padding: "7px 14px", fontSize: 12, color: "#69f0ae", fontWeight: 600 }}>✓ {success}</div>}
        </div>

        {/* ── MAIN LAYOUT ─────────────────────────────────────────── */}
        <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>

          {/* ── PANEL IZQUIERDO — lista de servicios ──────────────── */}
          <div style={{ width: 240, background: "#1e3d54", display: "flex", flexDirection: "column", flexShrink: 0, overflowY: "auto" }}>

            {/* Buscador servicios */}
            <div style={{ padding: "12px 14px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <input
                style={{ ...inputSx, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 11, padding: "7px 10px" }}
                placeholder="🔍  Buscar servicio..."
                value={svcSearch}
                onChange={e => setSvcSearch(e.target.value)}
              />
            </div>

            {/* Lista */}
            <div style={{ flex: 1, padding: "8px 0" }}>
              {filteredSvcs.map(svc => {
                const isActive = svc.id === selectedServiceId;
                const fieldCount = svc.fields?.length || 0;
                return (
                  <button key={svc.id} onClick={() => setSelectedServiceId(svc.id)} style={{ width: "100%", textAlign: "left", padding: "9px 16px", border: "none", background: isActive ? "rgba(231,164,50,0.15)" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, borderLeft: `3px solid ${isActive ? "#E7A432" : "transparent"}`, transition: "all 0.12s" }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                  >
                    <span style={{ fontSize: 11, fontWeight: isActive ? 700 : 500, color: isActive ? "#E7A432" : "rgba(255,255,255,0.65)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{svc.service_name}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 10, background: fieldCount > 0 ? (isActive ? "rgba(231,164,50,0.2)" : "rgba(255,255,255,0.08)") : "transparent", color: isActive ? "#E7A432" : "rgba(255,255,255,0.3)", flexShrink: 0 }}>{fieldCount > 0 ? fieldCount : ""}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── PANEL DERECHO — campos del servicio ───────────────── */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

            {/* Header servicio seleccionado */}
            {selectedSvc && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                <div>
                  <h2 style={{ margin: "0 0 3px", fontSize: 17, fontWeight: 800, color: "#163244" }}>{selectedSvc.service_name}</h2>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: 11, color: "#7a93a4" }}>{fields.length} campo{fields.length !== 1 ? "s" : ""} configurado{fields.length !== 1 ? "s" : ""}</span>
                    {/* Stats por tipo */}
                    {Object.entries(typeStats).map(([t, count]) => {
                      const cfg = TYPE_CFG[t] || TYPE_CFG.text;
                      return <span key={t} style={{ padding: "1px 7px", borderRadius: 20, fontSize: 9, fontWeight: 700, background: cfg.color + "12", color: cfg.color }}>{t}: {count}</span>;
                    })}
                  </div>
                </div>
                {!newField && (
                  <button onClick={() => setNewField(emptyField(selectedServiceId))} style={{ padding: "9px 20px", borderRadius: 50, border: "none", background: "#E7A432", color: "#163244", fontFamily: font, fontWeight: 800, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                    + Agregar campo
                  </button>
                )}
              </div>
            )}

            {loading ? (
              <div style={{ textAlign: "center", padding: "60px", color: "#7a93a4" }}>
                <span style={{ display: "inline-block", width: 24, height: 24, border: "3px solid #dce5ec", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", marginBottom: 10 }} /><br/>Cargando campos…
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

                {/* Nuevo campo — arriba si hay campos, para no hacer scroll */}
                {newField && (
                  <div style={{ marginBottom: 4 }}>
                    <FieldForm draft={newField} onChange={setNewField} onSave={handleCreateField} onCancel={() => setNewField(null)} saving={saving} title={`Nuevo campo para "${selectedSvc?.service_name}"`} />
                  </div>
                )}

                {/* Campos existentes */}
                {fields.length === 0 && !newField ? (
                  <div style={{ background: "#fff", borderRadius: 14, border: "2px dashed rgba(0,0,0,0.08)", padding: "48px 24px", textAlign: "center" }}>
                    <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#163244", marginBottom: 6 }}>Sin campos configurados</div>
                    <div style={{ fontSize: 12, color: "#7a93a4", marginBottom: 20 }}>Este servicio no tiene campos todavía. Agregá el primero.</div>
                    <button onClick={() => setNewField(emptyField(selectedServiceId))} style={{ padding: "10px 24px", borderRadius: 50, border: "none", background: "#163244", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>+ Agregar primer campo</button>
                  </div>
                ) : (
                  <>
                    {fields.length > 1 && !editingId && !newField && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", marginBottom: 2 }}>
                        <span style={{ fontSize: 10, color: "#aab8c2" }}>⠿</span>
                        <span style={{ fontSize: 10, color: "#aab8c2", fontWeight: 500 }}>Arrastrá las cards para reordenar los campos del formulario</span>
                      </div>
                    )}
                    {fields.map((field, index) => (
                      <FieldCard
                        key={field.id}
                        field={field}
                        index={index}
                        isEditing={editingId === field.id}
                        editDraft={editDraft}
                        setEditDraft={setEditDraft}
                        onEdit={() => { setEditingId(field.id); setEditDraft({ ...field }); setNewField(null); }}
                        onCancelEdit={() => setEditingId(null)}
                        onSave={() => handleSaveEdit(field.id)}
                        onDelete={() => handleDelete(field.id)}
                        saving={saving}
                        isDragging={dragIdx === index}
                        isDragOver={dragOverIdx === index}
                        onDragStart={() => handleDragStart(index)}
                        onDragEnter={() => setDragOverIdx(index)}
                        onDragOver={e => e.preventDefault()}
                        onDragLeave={() => setDragOverIdx(null)}
                        onDrop={() => handleDrop(index)}
                        onDragEnd={handleDragEnd}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── PANEL DERECHO — editor + preview ─────────────────── */}
          <div style={{ width: 320, borderLeft: "1px solid rgba(0,0,0,0.07)", display: "flex", flexDirection: "column", flexShrink: 0, overflow: "hidden" }}>

            {/* Editor de campo seleccionado */}
            {(editingId || newField) ? (
              <div style={{ flex: "0 0 auto", overflowY: "auto", padding: "14px", borderBottom: "1px solid rgba(0,0,0,0.07)", maxHeight: "55%" }}>
                <FieldForm
                  draft={newField || editDraft}
                  onChange={newField ? setNewField : setEditDraft}
                  onSave={newField ? handleCreateField : () => handleSaveEdit(editingId)}
                  onCancel={() => { setEditingId(null); setNewField(null); }}
                  saving={saving}
                  title={newField ? `Nuevo campo — "${selectedSvc?.service_name}"` : `Editando: ${fields.find(f=>f.id===editingId)?.label || ""}`}
                />
              </div>
            ) : (
              <div style={{ padding: "14px", borderBottom: "1px solid rgba(0,0,0,0.07)", background: "#f8fafb" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#7a93a4", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Editor</div>
                <div style={{ fontSize: 11, color: "#aab8c2" }}>Hacé clic en ✏ de un campo para editarlo, o usá "+ Agregar campo".</div>
              </div>
            )}

            {/* Preview del formulario */}
            <div style={{ flex: 1, overflowY: "auto", background: "#f5f7fa" }}>
              {/* Header preview */}
              <div style={{ padding: "10px 14px", borderBottom: "1px solid rgba(0,0,0,0.07)", background: "#fff", display: "flex", alignItems: "center", gap: 7 }}>
                <div style={{ width: 3, height: 12, background: "#E7A432", borderRadius: 2 }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: "#163244" }}>Preview del formulario</span>
                <span style={{ fontSize: 9, color: "#aab8c2", marginLeft: "auto" }}>Vista del cliente</span>
              </div>

              <div style={{ padding: "14px" }}>
                {fields.length === 0 && !newField ? (
                  <div style={{ textAlign: "center", padding: "32px 16px", color: "#aab8c2", fontSize: 12 }}>
                    <div style={{ fontSize: 24, marginBottom: 8 }}>👁</div>
                    Sin campos que previsualizar
                  </div>
                ) : (
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid rgba(0,0,0,0.07)", overflow: "hidden" }}>
                    {/* Header del form simulado */}
                    <div style={{ background: "#163244", padding: "12px 16px" }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#fff" }}>{selectedSvc?.service_name}</div>
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>Completá el formulario para solicitar el servicio</div>
                    </div>
                    <div style={{ padding: "14px", display: "flex", flexDirection: "column", gap: 10 }}>

                      {/* Campos guardados */}
                      {fields.map(f => <PreviewField key={f.id} field={f} isActive={editingId === f.id} />)}

                      {/* Campo nuevo siendo editado — preview en tiempo real */}
                      {newField && newField.label && (
                        <div style={{ border: "2px dashed #E7A432", borderRadius: 8, padding: "2px", background: "rgba(231,164,50,0.03)" }}>
                          <div style={{ fontSize: 8, fontWeight: 700, color: "#E7A432", textTransform: "uppercase", letterSpacing: "0.05em", padding: "4px 8px 0" }}>Nuevo campo</div>
                          <PreviewField field={newField} isNew />
                        </div>
                      )}

                      {/* Botón simulado */}
                      <div style={{ paddingTop: 4 }}>
                        <div style={{ width: "100%", padding: "10px", borderRadius: 50, background: "#163244", color: "#fff", textAlign: "center", fontSize: 12, fontWeight: 700 }}>
                          Enviar solicitud →
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      <style>{`
        * { box-sizing: border-box; }
        input:focus, select:focus { border-color: #163244 !important; outline: none; box-shadow: 0 0 0 3px rgba(22,50,68,0.08); }
        input::placeholder { color: rgba(255,255,255,0.3) !important; }
        @keyframes spin { to { transform: rotate(360deg); } }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); border-radius: 2px; }
      `}</style>
    </>
  );
}
